// src/components/webinar_funnel/sections/DataExplorer.jsx
import React, { useState, useMemo } from 'react';
import {
  FunnelIcon,
  EyeIcon,
  ChartBarIcon,
  ChevronDownIcon,
  CheckIcon,
} from '@heroicons/react/24/outline';
import TableControls from '../CampaignPerformance/TableControls';
import {
  useHeatmap,
  getHeatmapColor,
  HeatmapLegend,
} from '../../../../utils/heatMapUtils';

// ─── Field definitions ────────────────────────────────────────────────────────

const FIELD_CATEGORIES = {
  invitations: [
    { key: 'calls.cold',            label: 'Cold Calls',       category: 'Invitations' },
    { key: 'calls.linkedin_invites',label: 'LinkedIn',         category: 'Invitations' },
    { key: 'calls.non_joiners',     label: 'Non-joiners',      category: 'Invitations' },
    { key: 'calls.expansion',       label: 'Expansion',        category: 'Invitations' },
  ],
  registrations: [
    { key: 'registrations.coldCalls', label: 'Cold Calls Yes', category: 'Registrations' },
    { key: 'registrations.maybe',     label: 'Maybe',          category: 'Registrations' },
    { key: 'registrations.linkedin',  label: 'LinkedIn',       category: 'Registrations' },
    { key: 'registrations.zoom',      label: 'Zoom',           category: 'Registrations' },
  ],
  attendees: [
    { key: 'attendees.coldCalls',     label: 'Cold Calls',     category: 'Attendees' },
    { key: 'attendees.non_joiners',   label: 'Non-joiners',    category: 'Attendees' },
    { key: 'attendees.linkedin',      label: 'LinkedIn',       category: 'Attendees' },
    { key: 'attendees.other',         label: 'Other',          category: 'Attendees' },
    { key: 'attendees.uniqueViewers', label: 'Unique Viewers', category: 'Attendees' },
  ],
  bookings: [
    { key: 'bookings.webinar', label: 'Webinar',  category: 'Calls Booked' },
    { key: 'bookings.calls',   label: 'Website',  category: 'Calls Booked' },
    { key: 'bookings.email',   label: 'Email/SMS',category: 'Calls Booked' },
    { key: 'bookings.other',   label: 'Other',    category: 'Calls Booked' },
  ],
  conversions: [
    { key: 'conversions.inviteToReg',  label: 'Registration %', category: 'Conversions', isPercentage: true },
    { key: 'conversions.regToAttend',  label: 'Attendance %',   category: 'Conversions', isPercentage: true },
    { key: 'conversions.attendToBook', label: 'Booking %',      category: 'Conversions', isPercentage: true },
    { key: 'conversions.bookedToShow', label: 'Show Rate',      category: 'Conversions', isPercentage: true },
  ],
  sales: [
    { key: 'sales.show',      label: 'Showed',    category: 'Sales' },
    { key: 'sales.noShow',    label: 'No Show',   category: 'Sales' },
    { key: 'sales.qualified', label: 'Qualified', category: 'Sales' },
    { key: 'sales.total',     label: 'Sales',     category: 'Sales' },
  ],
};

const ALL_FIELDS = Object.values(FIELD_CATEGORIES).flat();

const AVAILABLE_METRICS = [
  { key: 'invitations',   label: 'Invitations',   color: 'text-darkblue-500' },
  { key: 'registrations', label: 'Registrations', color: 'text-violet-700' },
  { key: 'attendees',     label: 'Attendees',     color: 'text-teal-700' },
  { key: 'bookings',      label: 'Calls Booked',  color: 'text-brand-600' },
  { key: 'sales',         label: 'Sales',         color: 'text-teal-700' },
  { key: 'revenue',       label: 'Revenue',       color: 'text-brightblue-700' },
];

const FIELD_ORDER = [
  'calls.cold','calls.linkedin_invites','calls.non_joiners','calls.expansion',
  'registrations.coldCalls','registrations.maybe','registrations.linkedin','registrations.zoom',
  'attendees.coldCalls','attendees.non_joiners','attendees.linkedin','attendees.other','attendees.uniqueViewers',
  'bookings.webinar','bookings.calls','bookings.email','bookings.other',
  'conversions.inviteToReg','conversions.regToAttend','conversions.attendToBook','conversions.bookedToShow',
  'sales.show','sales.noShow','sales.qualified','sales.total',
];

const METRIC_ORDER = ['invitations','registrations','attendees','bookings','sales','revenue'];

// ─── Helpers ──────────────────────────────────────────────────────────────────

const getNestedValue = (obj, path) =>
  path.split('.').reduce((cur, k) => (cur && cur[k] !== undefined ? cur[k] : 0), obj);

const getPercentageValue = (row, idx) => {
  if (!row.conversions) return 0;
  const v = [row.conversions.inviteToReg, row.conversions.regToAttend, row.conversions.attendToBook][idx] ?? 0;
  const n = typeof v === 'string' ? parseFloat(v) : v || 0;
  return isNaN(n) ? 0 : n;
};

// ─── Selector dropdown ────────────────────────────────────────────────────────

const SelectorDropdown = ({ label, Icon, count, isOpen, onToggle, children }) => (
  <div>
    <label className="block text-xs font-semibold text-darkblue-500 mb-1.5">
      <Icon className="inline w-3.5 h-3.5 mr-1 mb-0.5" />
      {label}
    </label>
    <div className="relative">
      <button
        onClick={onToggle}
        className="w-full h-9 px-3 border border-grey-300 rounded-lg text-xs bg-white text-left flex items-center justify-between hover:border-grey-400 focus:outline-none focus:ring-2 focus:ring-brightblue-300 text-darkblue-500"
      >
        <span>{count === 0 ? `No ${label.toLowerCase()} selected` : `${count} selected`}</span>
        <ChevronDownIcon className={`w-3.5 h-3.5 text-grey-400 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {isOpen && (
        <div className="absolute top-full left-0 mt-1 w-full bg-white border border-grey-200 rounded-xl shadow-lg z-50 max-h-72 overflow-y-auto">
          {children}
        </div>
      )}
    </div>
  </div>
);

// ─── Main component ───────────────────────────────────────────────────────────

const DataExplorer = ({ data }) => {
  // Filter state
  const [filterType,        setFilterType]        = useState('all');
  const [dateRange,         setDateRange]         = useState('all');
  const [selectedWebinars,  setSelectedWebinars]  = useState(new Set());
  const [customStartDate,   setCustomStartDate]   = useState('');
  const [customEndDate,     setCustomEndDate]     = useState('');
  const [showFilterDropdown,setShowFilterDropdown]= useState(false);
  const [sortBy,            setSortBy]            = useState('date');
  const [sortOrder,         setSortOrder]         = useState('desc');

  // Heatmap state
  const [heatmapEnabled, setHeatmapEnabled] = useState(false);
  const [scalingType,    setScalingType]    = useState('column-wise');

  // Explorer-specific state
  const [selectedFields,      setSelectedFields]      = useState(new Set());
  const [showFieldDropdown,   setShowFieldDropdown]   = useState(false);
  const [selectedMetrics,     setSelectedMetrics]     = useState(new Set(['registrations','attendees','bookings']));
  const [showMetricDropdown,  setShowMetricDropdown]  = useState(false);
  const [dateGrouping,        setDateGrouping]        = useState('daily');

  // ── Dynamic columns ────────────────────────────────────────────────────────
  const dynamicColumns = useMemo(() => {
    const cols = [
      { key: 'campaign', label: 'Campaign', type: 'info' },
      { key: 'date',     label: 'Date',     type: 'info' },
    ];

    let currentSection = '';
    FIELD_ORDER.forEach((fk) => {
      if (!selectedFields.has(fk)) return;
      const info = ALL_FIELDS.find((f) => f.key === fk) || { key: fk, label: fk, category: 'Other' };
      const firstInSection = info.category !== currentSection;
      currentSection = info.category;
      cols.push({ key: fk, label: info.label, type: 'field', category: info.category, isPercentage: info.isPercentage, firstInSection });
    });

    let firstMetric = true;
    METRIC_ORDER.forEach((mk) => {
      if (!selectedMetrics.has(mk)) return;
      const m = AVAILABLE_METRICS.find((x) => x.key === mk);
      if (!m) return;
      cols.push({ key: mk, label: m.label, type: 'metric', color: m.color, firstInSection: firstMetric });
      firstMetric = false;
    });

    return cols;
  }, [selectedFields, selectedMetrics]);

  // ── Filter + sort ──────────────────────────────────────────────────────────
  const filteredData = useMemo(() => {
    let out = data || [];

    if (filterType === 'webinars' && selectedWebinars.size > 0)
      out = out.filter((c) => selectedWebinars.has(c.id.toString()));

    if (filterType === 'dateRange') {
      const now = new Date();
      let start, end;
      if (dateRange === '7days')  start = new Date(now - 7 * 864e5);
      if (dateRange === '30days') start = new Date(now - 30 * 864e5);
      if (dateRange === '90days') start = new Date(now - 90 * 864e5);
      if (dateRange === 'custom') {
        if (customStartDate) start = new Date(customStartDate);
        if (customEndDate)   end   = new Date(customEndDate);
      }
      if (start || end) {
        out = out.filter((c) => {
          const d = new Date(c.date);
          if (start && d < start) return false;
          if (end   && d > end)   return false;
          return true;
        });
      }
    }

    return [...out].sort((a, b) => {
      let av, bv;
      switch (sortBy) {
        case 'registrations': av = a.registrations?.total || 0; bv = b.registrations?.total || 0; break;
        case 'attendees':     av = a.attendees?.total    || 0; bv = b.attendees?.total    || 0; break;
        case 'bookings':      av = a.bookings?.total     || 0; bv = b.bookings?.total     || 0; break;
        default:              av = new Date(a.date);           bv = new Date(b.date);
      }
      return sortOrder === 'desc' ? (bv > av ? 1 : -1) : (av > bv ? 1 : -1);
    });
  }, [data, filterType, dateRange, selectedWebinars, customStartDate, customEndDate, sortBy, sortOrder]);

  // ── Date grouping ──────────────────────────────────────────────────────────
  const groupedData = useMemo(() => {
    if (dateGrouping === 'daily') return filteredData;

    const grouped = {};
    filteredData.forEach((c) => {
      const d = new Date(c.date);
      if (isNaN(d)) return;

      let key;
      if (dateGrouping === 'weekly') {
        const ws = new Date(d); ws.setDate(d.getDate() - d.getDay()); ws.setHours(0,0,0,0);
        key = ws.toISOString().split('T')[0];
      } else {
        key = `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}`;
      }

      if (!grouped[key]) {
        grouped[key] = { id: key, date: key, campaigns: [], invitations: 0,
          registrations: { total: 0 }, attendees: { total: 0 }, bookings: { total: 0 },
          sales: { total: 0, revenue: 0 }, conversions: { inviteToReg: 0, regToAttend: 0, attendToBook: 0 } };
      }
      const g = grouped[key];
      g.campaigns.push(c);
      g.invitations          += Number(c.invitations)          || 0;
      g.registrations.total  += Number(c.registrations?.total) || 0;
      g.attendees.total      += Number(c.attendees?.total)     || 0;
      g.bookings.total       += Number(c.bookings?.total)      || 0;
      g.sales.total          += Number(c.sales?.total)         || 0;
      g.sales.revenue        += Number(c.sales?.revenue)       || 0;
    });

    Object.values(grouped).forEach((g) => {
      g.conversions.inviteToReg  = g.invitations          > 0 ? +((g.registrations.total / g.invitations)          * 100).toFixed(1) : 0;
      g.conversions.regToAttend  = g.registrations.total  > 0 ? +((g.attendees.total     / g.registrations.total)  * 100).toFixed(1) : 0;
      g.conversions.attendToBook = g.attendees.total      > 0 ? +((g.bookings.total      / g.attendees.total)      * 100).toFixed(1) : 0;
    });

    return Object.values(grouped).sort((a, b) => new Date(a.date) - new Date(b.date));
  }, [filteredData, dateGrouping]);

  // ── Heatmap ────────────────────────────────────────────────────────────────
  const processedData = useMemo(() =>
    groupedData.map((row) => ({
      ...row,
      conversions: { ...row.conversions,
        inviteToReg:  getPercentageValue(row, 0),
        regToAttend:  getPercentageValue(row, 1),
        attendToBook: getPercentageValue(row, 2),
      },
    })), [groupedData]);

  const heatmap = useHeatmap(processedData, [
    'conversions.inviteToReg','conversions.regToAttend','conversions.attendToBook',
  ], scalingType);

  React.useEffect(() => {
    if (heatmapEnabled !== undefined && heatmap.enabled !== heatmapEnabled) heatmap.setEnabled(heatmapEnabled);
  }, [heatmapEnabled, heatmap]);

  React.useEffect(() => {
    if (scalingType !== undefined && heatmap.scalingType !== scalingType) heatmap.setScalingType(scalingType);
  }, [scalingType, heatmap]);

  const getHeatmapStyle = (value, colIdx) => {
    if (!heatmap.enabled) return { backgroundColor: 'transparent', color: 'inherit' };
    return { ...getHeatmapColor(value, colIdx, heatmap.scalingInfo, { useWhiteText: false, textColor: '#152451' }) };
  };

  // ── Cell value ─────────────────────────────────────────────────────────────
  const getCellValue = (item, col) => {
    if (col.type === 'info') {
      if (col.key === 'campaign') {
        return dateGrouping === 'daily'
          ? (item.campaign_name || `Campaign ${item.id}`)
          : dateGrouping === 'weekly' ? 'Weekly group' : 'Monthly group';
      }
      return new Date(item.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    }
    if (col.type === 'field') {
      const v = getNestedValue(item, col.key);
      return col.isPercentage ? `${v || '0.0'}%` : (v || 0).toLocaleString();
    }
    if (col.type === 'metric') {
      switch (col.key) {
        case 'invitations':   return (item.invitations || 0).toLocaleString();
        case 'registrations': return (item.registrations?.total || 0).toLocaleString();
        case 'attendees':     return (item.attendees?.total || 0).toLocaleString();
        case 'bookings':      return (item.bookings?.total || 0).toLocaleString();
        case 'sales':         return (item.sales?.total || 0).toLocaleString();
        case 'revenue':       return `$${(item.sales?.revenue || 0).toLocaleString()}`;
        default:              return '0';
      }
    }
    return '';
  };

  // ── Toggle helpers ─────────────────────────────────────────────────────────
  const toggleField  = (k) => { const s = new Set(selectedFields);  s.has(k) ? s.delete(k) : s.add(k); setSelectedFields(s); };
  const toggleMetric = (k) => { const s = new Set(selectedMetrics); s.has(k) ? s.delete(k) : s.add(k); setSelectedMetrics(s); };

  const thCls = 'px-4 py-3 text-xs font-bold text-white uppercase tracking-widest text-center';

  return (
    <div className="space-y-0">

      {/* ── Shared toolbar ────────────────────────────────────────────────── */}
      <TableControls
        filterType={filterType}            setFilterType={setFilterType}
        dateRange={dateRange}              setDateRange={setDateRange}
        selectedWebinars={selectedWebinars}setSelectedWebinars={setSelectedWebinars}
        customStartDate={customStartDate}  setCustomStartDate={setCustomStartDate}
        customEndDate={customEndDate}      setCustomEndDate={setCustomEndDate}
        showFilterDropdown={showFilterDropdown} setShowFilterDropdown={setShowFilterDropdown}
        sortBy={sortBy}                    setSortBy={setSortBy}
        sortOrder={sortOrder}              setSortOrder={setSortOrder}
        data={data}
        heatmapEnabled={heatmapEnabled}
        onHeatmapToggle={() => setHeatmapEnabled((v) => !v)}
        scalingType={scalingType}
        onScalingTypeChange={setScalingType}
      />

      {/* ── Explorer controls ─────────────────────────────────────────────── */}
      <div className="px-4 py-3 md:px-6 bg-grey-50 border-b border-grey-200">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">

          {/* Field selector */}
          <SelectorDropdown
            label="Select Fields" Icon={FunnelIcon}
            count={selectedFields.size}
            isOpen={showFieldDropdown}
            onToggle={() => setShowFieldDropdown((v) => !v)}
          >
            <div className="px-3 py-2 bg-grey-50 border-b border-grey-200 flex justify-between items-center sticky top-0">
              <span className="text-xs text-grey-400">Choose fields to display</span>
              <button onClick={() => { setSelectedFields(new Set()); setShowFieldDropdown(false); }}
                className="text-xs text-brightblue-600 font-medium hover:text-brightblue-700">
                Clear all
              </button>
            </div>
            <div className="p-3 space-y-3">
              {Object.entries(FIELD_CATEGORIES).map(([catKey, fields]) => (
                <div key={catKey}>
                  <div className="text-xs font-bold uppercase tracking-widest text-grey-400 mb-1.5">
                    {fields[0]?.category || catKey}
                  </div>
                  <div className="space-y-1">
                    {fields.map((f) => (
                      <label key={f.key} className="flex items-center gap-2 cursor-pointer hover:bg-grey-50 px-1 py-0.5 rounded">
                        <input type="checkbox" checked={selectedFields.has(f.key)} onChange={() => toggleField(f.key)}
                          className="rounded border-grey-300 text-brightblue-500 w-3 h-3" />
                        <span className="text-xs text-darkblue-500">{f.label}</span>
                        {f.isPercentage && (
                          <span className="text-xs text-brightblue-500 font-medium">%</span>
                        )}
                      </label>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </SelectorDropdown>

          {/* Metric selector */}
          <SelectorDropdown
            label="Select Metrics" Icon={EyeIcon}
            count={selectedMetrics.size}
            isOpen={showMetricDropdown}
            onToggle={() => setShowMetricDropdown((v) => !v)}
          >
            <div className="px-3 py-2 bg-grey-50 border-b border-grey-200 flex justify-between items-center sticky top-0">
              <span className="text-xs text-grey-400">Totals to display</span>
              <button onClick={() => { setSelectedMetrics(new Set()); setShowMetricDropdown(false); }}
                className="text-xs text-brightblue-600 font-medium hover:text-brightblue-700">
                Clear all
              </button>
            </div>
            <div className="p-3 space-y-1">
              {AVAILABLE_METRICS.map((m) => (
                <label key={m.key} className="flex items-center gap-2 cursor-pointer hover:bg-grey-50 px-1 py-1 rounded">
                  <input type="checkbox" checked={selectedMetrics.has(m.key)} onChange={() => toggleMetric(m.key)}
                    className="rounded border-grey-300 text-brightblue-500 w-3 h-3" />
                  <span className={`text-xs font-medium ${m.color}`}>{m.label}</span>
                  {selectedMetrics.has(m.key) && <CheckIcon className="w-3 h-3 text-brightblue-500 ml-auto" />}
                </label>
              ))}
            </div>
          </SelectorDropdown>

          {/* Group by */}
          <div>
            <label className="block text-xs font-semibold text-darkblue-500 mb-1.5">
              <ChartBarIcon className="inline w-3.5 h-3.5 mr-1 mb-0.5" />
              Group By
            </label>
            <select
              value={dateGrouping}
              onChange={(e) => setDateGrouping(e.target.value)}
              className="w-full h-9 px-3 border border-grey-300 rounded-lg text-xs text-darkblue-500 bg-white focus:outline-none focus:ring-2 focus:ring-brightblue-300"
            >
              <option value="daily">Daily</option>
              <option value="weekly">Weekly</option>
              <option value="monthly">Monthly</option>
            </select>
          </div>
        </div>
      </div>

      {/* ── Table ─────────────────────────────────────────────────────────── */}
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-grey-200">
          <thead className="bg-darkblue-500">
            <tr>
              {dynamicColumns.map((col, i) => (
                <th
                  key={col.key}
                  className={`${thCls} ${col.type === 'info' ? 'text-left' : ''} ${
                    col.firstInSection && i > 1 ? 'border-l-2 border-darkblue-400' : ''
                  }`}
                >
                  {col.label}
                  {col.isPercentage && <span className="ml-1 text-brightblue-300 font-normal">%</span>}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-grey-100">
            {groupedData.map((item, rowIdx) => (
              <tr
                key={item.id || rowIdx}
                className={rowIdx % 2 === 0 ? 'bg-white hover:bg-grey-50' : 'bg-grey-50 hover:bg-grey-100'}
              >
                {dynamicColumns.map((col, colIdx) => (
                  <td
                    key={col.key}
                    className={`px-4 py-2 text-xs whitespace-nowrap ${
                      col.type === 'info' ? 'text-left font-semibold text-darkblue-500' : 'text-center text-darkblue-500'
                    } ${col.firstInSection && colIdx > 1 ? 'border-l-2 border-grey-200' : ''}`}
                  >
                    {getCellValue(item, col)}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>

        {groupedData.length === 0 && (
          <div className="p-8 text-center text-sm text-grey-400">
            No data available for the selected filters.
          </div>
        )}

        {selectedFields.size === 0 && selectedMetrics.size === 0 && (
          <div className="p-8 text-center text-sm text-grey-400">
            Select fields or metrics above to display data.
          </div>
        )}
      </div>

      {/* ── Footer summary ────────────────────────────────────────────────── */}
      {groupedData.length > 0 && (
        <div className="px-4 py-3 md:px-6 bg-grey-50 border-t border-grey-200 flex items-center justify-between">
          <span className="text-xs text-grey-500">
            Showing{' '}
            <span className="font-semibold text-darkblue-500">{groupedData.length}</span>{' '}
            {dateGrouping === 'daily' ? 'campaign' : dateGrouping === 'weekly' ? 'week' : 'month'}
            {groupedData.length !== 1 ? 's' : ''}
            {dateGrouping !== 'daily' && (
              <> from <span className="font-semibold text-darkblue-500">{filteredData.length}</span> campaign{filteredData.length !== 1 ? 's' : ''}</>
            )}
            {filteredData.length !== (data?.length || 0) && (
              <> —{' '}
                <span className="text-brightblue-600">filtered from {data?.length || 0} total</span>
              </>
            )}
          </span>
          {dynamicColumns.length > 2 && (
            <span className="text-xs font-medium text-violet-700">
              {dynamicColumns.length - 2} column{dynamicColumns.length - 2 !== 1 ? 's' : ''} displayed
            </span>
          )}
        </div>
      )}

      {/* ── Heatmap legend ────────────────────────────────────────────────── */}
      {heatmap.enabled && heatmap.scalingInfo && (
        <div className="px-4 py-4 md:px-6 bg-grey-50 border-t border-grey-200">
          <div className="flex justify-end mb-2">
            <span className="text-xs text-grey-400">
              Based on {groupedData.length} row{groupedData.length !== 1 ? 's' : ''}
              {filteredData.length !== (data?.length || 0) && <span className="ml-1">(filtered)</span>}
            </span>
          </div>
          <HeatmapLegend
            scalingInfo={heatmap.scalingInfo}
            fields={['Registration %', 'Attendance %', 'Booking %']}
          />
        </div>
      )}
    </div>
  );
};

export default DataExplorer;